import $ from 'jquery';
import ApplicationController from './application_controller';

/*
 * Mobile Nav Select Controller
 *
 * Collapses an overflowing nav into an Impulse <awc-autocomplete> dropdown on narrow
 * screens. Used for the patient page's top tabs and the vertical left sub-nav.
 *
 * The original nav stays the single source of truth. This controller wraps an EMPTY,
 * server-rendered Impulse::SelectComponent and populates it from the source nav's links
 * (located by `sourceValue`, a CSS selector). Choosing an option clicks the matching
 * link, so every existing behavior is reused — full-page routing for the top tabs and
 * Bootstrap pill toggling + URL fragments for the left pills.
 *
 * The displayed value is synced through the element's public setValue(). Because
 * <awc-autocomplete> initializes asynchronously (ImpulseElement awaits domReady + child
 * upgrades before building its select variant in connected()), setValue() isn't usable
 * the instant this controller connects. We therefore wait for Impulse's `data-impulse-element`
 * marker — stamped the moment the element finishes starting, just before connected() runs —
 * before calling setValue(). See whenSelectReady().
 *
 * If the source nav is absent on the current page (e.g. a patient sub-page with no left
 * sub-nav), the column removes itself so the sibling select fills the row.
 */
export default class extends ApplicationController {
  static targets = ['select'];

  static values = { source: String };

  connect() {
    const source = document.querySelector(this.sourceValue);
    this.links = source ? Array.from(source.querySelectorAll('a.nav-link')) : [];

    if (this.links.length === 0) {
      this.element.remove();
      return;
    }
    if (!this.hasSelectTarget) return;

    this.populateOptions();
    this.selectActiveLink();

    this.boundNavigate = this.navigate.bind(this);
    this.selectTarget.addEventListener('awc-autocomplete:commit', this.boundNavigate);

    // Keep the dropdown label in sync when the underlying pill is activated some other
    // way (a URL fragment on load, browser back/forward, etc.).
    this.boundOnShown = this.onShown.bind(this);
    $(document).on('shown.bs.tab', this.boundOnShown);
  }

  disconnect() {
    if (this.readyObserver) {
      this.readyObserver.disconnect();
      this.readyObserver = undefined;
    }
    if (this.boundNavigate && this.hasSelectTarget) {
      this.selectTarget.removeEventListener('awc-autocomplete:commit', this.boundNavigate);
    }
    if (this.boundOnShown) $(document).off('shown.bs.tab', this.boundOnShown);
  }

  populateOptions() {
    const container = this.selectTarget.querySelector('.awc-autocomplete-options');
    if (!container) return;
    container.innerHTML = '';
    this.links.forEach((link, index) => {
      container.appendChild(this.buildOption(index, this.labelFor(link)));
    });
  }

  selectActiveLink() {
    const activeIndex = this.links.findIndex((link) => link.classList.contains('active'));
    this.showSelection(activeIndex === -1 ? 0 : activeIndex);
  }

  // Sync the dropdown's displayed value via the element's public setValue(value, text).
  // setValue() does not emit awc-autocomplete:commit, so this updates the label without
  // triggering navigate(). We await the element's async upgrade first (see whenSelectReady).
  async showSelection(index) {
    const link = this.links[index];
    if (!link) return;
    await this.whenSelectReady();
    // The controller may have disconnected during the await (Turbo nav/preview swap).
    if (!this.hasSelectTarget) return;
    this.selectTarget.setValue(String(index), this.labelFor(link));
  }

  // Resolves once the <awc-autocomplete> instance has finished starting, so setValue() is
  // safe to call. Two gates, in order:
  //   1. customElements.whenDefined() — the class is registered (tracks registration only,
  //      not this instance's upgrade).
  //   2. the `data-impulse-element` marker — Impulse stamps it the instant the element
  //      finishes starting, just before connected() runs. setValue() delegates to the
  //      select variant built in connected(), which runs a few microtasks after insertion
  //      (it awaits domReady + child upgrades), so this per-instance signal is what we need.
  whenSelectReady() {
    if (this.readyPromise) return this.readyPromise;

    this.readyPromise = customElements.whenDefined(this.selectTarget.localName).then(() => {
      if (this.selectTarget.hasAttribute('data-impulse-element')) return undefined;

      return new Promise((resolve) => {
        this.readyObserver = new MutationObserver(() => {
          if (!this.selectTarget.hasAttribute('data-impulse-element')) return;
          this.readyObserver.disconnect();
          resolve();
        });
        this.readyObserver.observe(this.selectTarget, {
          attributes: true,
          attributeFilter: ['data-impulse-element'],
        });
      });
    });

    return this.readyPromise;
  }

  navigate(event) {
    const link = this.links[Number(event.detail && event.detail.value)];
    if (link) link.click();
  }

  onShown(event) {
    const index = this.links.indexOf(event.target);
    if (index !== -1) this.showSelection(index);
  }

  labelFor(link) {
    return link.textContent.replace(/\s+/g, ' ').trim();
  }

  buildOption(value, text) {
    const option = document.createElement('div');
    option.setAttribute('role', 'option');
    option.setAttribute('value', String(value));
    option.setAttribute('data-text', text);
    option.className = 'awc-autocomplete-option dropdown-item';

    const content = document.createElement('div');
    content.className = 'awc-autocomplete-option-content';
    const span = document.createElement('span');
    span.className = 'awc-autocomplete-option-text';
    span.textContent = text;
    content.appendChild(span);

    const check = document.createElement('i');
    check.className = 'bi bi-check2 awc-autocomplete-option-check';
    check.setAttribute('aria-hidden', 'true');

    option.appendChild(content);
    option.appendChild(check);
    return option;
  }
}
